/*
 * Copyright (C) 2019 - present Marek Kuzora - All Rights Reserved.
 */


import ParentEnum from 'galax/enum/parent';

import Direction from 'galax/server/location/direction';
import Neighbors from 'galax/server/location/neighbors';

import * as Coords from 'shared/geo/coords';


export const makeLocation = (maybeLocation, resolver) =>
{
  const location = (maybeLocation instanceof Coords.Axial) ? { id: maybeLocation, parent: null } : maybeLocation;

  return new Location(location, resolver);
}


export default class Location
{
  constructor (location, resolver)
  {
    this.location = location;
    this.resolver = resolver;

    this.neighbors = new Neighbors(this.getCoords(), resolver);
  }


  getZone ()
  {
    return this.resolver.getZone(this.getCoords(), this.location.parent);
  }


  getDock ()
  {
    return this.resolver.getDock(this.getCoords(), this.location.parent);
  }


  getPlatform ()
  {
    return this.resolver.getPlatform(this.getCoords(), this.location.parent);
  }


  getObject ()
  {
    const zone = this.getZone();

    if (zone)
    {
      return zone;
    }

    const platform = this.getPlatform();

    if (platform)
    {
      return platform;
    }

    const dock = this.getDock();

    if (dock)
    {
      return dock;
    }

    return null;
  }


  getUser ()
  {
    const object = this.getObject();

    return object ? object.getUser(this.resolver) : null;
  }


  getPlanet ()
  {
    const object = this.getObject();

    return object ? object.getPlanet() : null;
  }


  getInteractionsTo ()
  {
    const object = this.getObject();

    return object ? this.__upcastVectorToDirection__(object.getInteractionsTo()) : [];
  }


  getInteractionsFrom ()
  {
    const object = this.getObject();

    return object ? this.__upcastVectorToDirection__(object.getInteractionsFrom()) : [];
  }


  getProjectilesTo ()
  {
    const object = this.getObject();

    return object ? this.__upcastVectorToDirection__(object.getProjectilesTo()) : [];
  }


  getProjectilesFrom ()
  {
    const object = this.getObject();

    return object ? this.__upcastVectorToDirection__(object.getProjectilesFrom()) : [];
  }


  __upcastVectorToDirection__ (objects)
  {
    let collection = [];

    for (const object of objects)
    {
      collection.push(new Direction(object, this.resolver));
    }

    return collection;
  }


  getCass ()
  {
    if (this.getZone())
    {
      return this.getZone().getCass().getLocation();
    }

    if (this.getPlatform())
    {
      return this.getPlatform().getCass().location;
    }

    // TODO something for the docks too?

    return { id: this.getCoords().toString(), parent: null };
  }


  isEmpty ()
  {
    return this.getObject() == null;
  }


  isLocal ()
  {
    return this.resolver.isLocal();
  }


  getNeighbors ()
  {
    return this.neighbors;
  }


  getCoords ()
  {
    return this.location.id;
  }


  getResolver ()
  {
    return this.resolver;
  }


  getID ()
  {
    return this.getCoords().toString();
  }


  getParentID ()
  {
    const { parent } = this.getCass();

    return parent ? parent.id : null;
  }


  equals (other)
  {
    return this.getCoords().equals(other.getCoords());
  }


  less (other)
  {
    return this.getCoords().less(other.getCoords());
  }


  valueOf ()
  {
    return this.getID();
  }


}
